/**
 * AgentObservability — BUSINESS-AUTONOMY Phase 2.
 *
 * Six self-contained tiles dropped into each per-department hub page:
 *   <ActiveAgentsCard />     — agent_status rows + paused/running pills
 *   <SessionsPanel />        — recent sessions across the dept's agents
 *   <ActivityStream />       — live-tail audit log entries
 *   <DeptQueuePanel />       — workflow_items filtered by department, with
 *                               status filter chips (mirrors engineering page,
 *                               commit 9f77cd80)
 *   <EscalationsCard />      — workflow_items needing manager attention
 *   <BudgetGauge />          — budget_usd_spent across the dept's agents
 *
 * All tiles take a `department` prop and self-load. Empty-state copy is the
 * canonical Phase 2 string: "No agents active yet · Phase 2 of business-
 * autonomy build" so the dept page renders cleanly even before Phase 3+
 * coordinators are wired.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Card, Endpoint, Pill, StatusDot } from './index'
import { formatCurrency } from '@/utils/formatCurrency'
import {
  hubAutonomy, hubQueue,
  type HubAutonomyAgent, type HubAutonomyAgentSession,
  type HubAutonomyAgentActivity, type HubDeptEscalation,
  type HubQueueItem,
} from '../api'

const REFRESH_MS = 30_000
const PHASE2_EMPTY = 'No agents active yet · Phase 2 of business-autonomy build'

// ── ActiveAgentsCard ─────────────────────────────────────────────────────

export function ActiveAgentsCard({ department }: { department: string }) {
  const [agents, setAgents] = useState<HubAutonomyAgent[] | null>(null)
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const r = await hubAutonomy.agents({ department }).catch(() => null)
      if (!cancelled) setAgents(r?.agents ?? [])
    }
    load()
    const id = setInterval(load, REFRESH_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [department])

  return (
    <Card title="Active agents" endpoint={`/hub/agents?department=${department}`} bodyPad={false}>
      {agents === null && (
        <div style={{ padding: '14px 12px', color: 'var(--text-dim)', fontSize: 12 }}>Loading…</div>
      )}
      {agents !== null && agents.length === 0 && (
        <div style={{ padding: '14px 12px', color: 'var(--text-dim)', fontSize: 12 }}>{PHASE2_EMPTY}</div>
      )}
      {agents !== null && agents.length > 0 && agents.map((a, i) => (
        <div
          key={a.agent_id}
          className="row"
          style={{
            padding: '10px 12px',
            borderTop: i ? '1px solid var(--border)' : 0,
            gap: 10,
          }}
        >
          <StatusDot tone={statusTone(a)} />
          <span style={{ flex: 1, fontSize: 12.5 }}>{a.agent_id}</span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            {a.action_count_24h} acts/24h
          </span>
          <Pill variant={a.paused ? 'warn' : a.current_status === 'errored' ? 'err' : 'ok'}>
            {a.paused ? 'paused' : a.current_status}
          </Pill>
        </div>
      ))}
    </Card>
  )
}

function statusTone(a: HubAutonomyAgent): 'ok' | 'warn' | 'err' | 'info' {
  if (a.current_status === 'errored') return 'err'
  if (a.paused) return 'warn'
  if (a.current_status === 'running') return 'ok'
  return 'info'
}

// ── SessionsPanel ────────────────────────────────────────────────────────

export function SessionsPanel({ department }: { department: string }) {
  const [sessions, setSessions] = useState<HubAutonomyAgentSession[] | null>(null)
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const ar = await hubAutonomy.agents({ department }).catch(() => null)
      const list = ar?.agents ?? []
      if (list.length === 0) {
        if (!cancelled) setSessions([])
        return
      }
      const all: HubAutonomyAgentSession[] = []
      for (const a of list) {
        const r = await hubAutonomy.sessions(a.agent_id, 20).catch(() => null)
        if (r?.sessions) all.push(...r.sessions)
      }
      all.sort((x, y) => (x.started_at < y.started_at ? 1 : -1))
      if (!cancelled) setSessions(all.slice(0, 30))
    }
    load()
    const id = setInterval(load, REFRESH_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [department])

  return (
    <Card title="Sessions" endpoint={`/hub/agents/:id/sessions`} bodyPad={false}>
      {sessions === null && (
        <div style={{ padding: '14px 12px', color: 'var(--text-dim)', fontSize: 12 }}>Loading…</div>
      )}
      {sessions !== null && sessions.length === 0 && (
        <div style={{ padding: '14px 12px', color: 'var(--text-dim)', fontSize: 12 }}>{PHASE2_EMPTY}</div>
      )}
      {sessions !== null && sessions.length > 0 && sessions.map((s, i) => (
        <div
          key={s.id}
          className="row"
          style={{
            padding: '8px 12px',
            borderTop: i ? '1px solid var(--border)' : 0,
            gap: 10,
          }}
        >
          <Pill variant={sessionTone(s.status)}>{s.status}</Pill>
          <span style={{ flex: 1, fontSize: 12 }}>{s.agent_id}</span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            {s.action_count} acts
          </span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            {relTime(s.started_at)}
          </span>
        </div>
      ))}
    </Card>
  )
}

function sessionTone(status: string): 'admin' | 'live' | 'ok' | 'warn' | 'err' | 'info' | 'ghost' {
  if (status === 'errored') return 'err'
  if (status === 'paused') return 'warn'
  if (status === 'running') return 'live'
  if (status === 'completed') return 'ok'
  return 'ghost'
}

// ── ActivityStream ───────────────────────────────────────────────────────

export function ActivityStream({ department }: { department: string }) {
  const [activity, setActivity] = useState<HubAutonomyAgentActivity[] | null>(null)
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const ar = await hubAutonomy.agents({ department }).catch(() => null)
      const list = ar?.agents ?? []
      if (list.length === 0) {
        if (!cancelled) setActivity([])
        return
      }
      const all: HubAutonomyAgentActivity[] = []
      for (const a of list) {
        const r = await hubAutonomy.activity(a.agent_id, 30).catch(() => null)
        if (r?.activity) all.push(...r.activity)
      }
      all.sort((x, y) => (x.created_at < y.created_at ? 1 : -1))
      if (!cancelled) setActivity(all.slice(0, 50))
    }
    load()
    const id = setInterval(load, REFRESH_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [department])

  return (
    <Card title="Activity stream" endpoint={`/hub/agents/:id/activity`} bodyPad={false}>
      {activity === null && (
        <div style={{ padding: '14px 12px', color: 'var(--text-dim)', fontSize: 12 }}>Loading…</div>
      )}
      {activity !== null && activity.length === 0 && (
        <div style={{ padding: '14px 12px', color: 'var(--text-dim)', fontSize: 12 }}>{PHASE2_EMPTY}</div>
      )}
      {activity !== null && activity.length > 0 && activity.map((a, i) => (
        <div
          key={a.id}
          className="row"
          style={{
            padding: '6px 12px',
            borderTop: i ? '1px solid var(--border)' : 0,
            gap: 8,
            alignItems: 'baseline',
          }}
        >
          <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)', width: 80 }}>
            {relTime(a.created_at)}
          </span>
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{a.agent_id}</span>
          <span style={{ flex: 1, fontSize: 12 }}>{a.action}</span>
          {a.target_table && (
            <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>
              {a.target_table}{a.target_id ? `:${a.target_id}` : ''}
            </span>
          )}
        </div>
      ))}
    </Card>
  )
}

// ── DeptQueuePanel ───────────────────────────────────────────────────────
// Mirrors engineering page's status filter chip pattern (commit 9f77cd80).

const QUEUE_STATUSES = ['queued', 'in_progress', 'in_review', 'blocked', 'done'] as const
type QueueStatus = typeof QUEUE_STATUSES[number]

export function DeptQueuePanel({ department }: { department: string }) {
  const [filter, setFilter] = useState<QueueStatus>('queued')
  const [items, setItems] = useState<HubQueueItem[] | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const r = await hubQueue.list({ status: filter, limit: 100 }).catch(() => null)
      if (!cancelled) setItems(r?.items ?? [])
    }
    load()
    const id = setInterval(load, REFRESH_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [filter])

  // Filter to dept after fetch — workflow_items.department default 'eng'
  // means non-eng dept pages will mostly show empty until coordinators land.
  const filtered = useMemo(() => {
    if (!items) return null
    return items.filter(it => {
      const dept = (it.metadata?.department as string) || extractDept(it)
      return dept === department
    })
  }, [items, department])

  return (
    <Card
      title="Queue"
      endpoint={`/hub/queue?status=${filter}`}
      bodyPad={false}
      right={
        <div style={{ display: 'flex', gap: 4 }}>
          {QUEUE_STATUSES.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`btn ${filter === s ? 'primary' : 'ghost'}`}
              style={{ height: 22, fontSize: 10.5, padding: '0 8px' }}
            >
              {s}
            </button>
          ))}
        </div>
      }
    >
      {filtered === null && (
        <div style={{ padding: '14px 12px', color: 'var(--text-dim)', fontSize: 12 }}>Loading…</div>
      )}
      {filtered !== null && filtered.length === 0 && (
        <div style={{ padding: '14px 12px', color: 'var(--text-dim)', fontSize: 12 }}>
          No {filter} items in {department}.
        </div>
      )}
      {filtered !== null && filtered.length > 0 && filtered.slice(0, 20).map((it, i) => (
        <div
          key={it.id}
          className="row"
          style={{
            padding: '8px 12px',
            borderTop: i ? '1px solid var(--border)' : 0,
            gap: 10,
          }}
        >
          <Pill variant={priorityTone(it.priority)}>{it.priority}</Pill>
          <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)', minWidth: 100 }}>
            {it.row_id || it.id.slice(0, 8)}
          </span>
          <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {it.title}
          </span>
          {it.safety && it.safety !== 'green' && (
            <Pill variant={safetyTone(it.safety)}>{it.safety}</Pill>
          )}
        </div>
      ))}
    </Card>
  )
}

function extractDept(it: HubQueueItem): string {
  // workflow_items.department isn't on the JSON wire format yet; fall back to
  // metadata.area as a soft signal, then 'eng' default.
  const area = (it.metadata?.area as string) || ''
  if (area === 'autonomy' || area === 'eng' || area === 'engineering') return 'eng'
  if (area === 'sales' || area === 'marketing' || area === 'customers' || area === 'support' || area === 'qa') return area
  return 'eng'
}

function priorityTone(p: string): 'admin' | 'live' | 'ok' | 'warn' | 'err' | 'info' | 'ghost' {
  if (p === 'p0') return 'err'
  if (p === 'p1') return 'warn'
  return 'ghost'
}

function safetyTone(s: string): 'admin' | 'live' | 'ok' | 'warn' | 'err' | 'info' | 'ghost' {
  if (s === 'blocked') return 'err'
  if (s === 'red') return 'err'
  if (s === 'yellow') return 'warn'
  return 'ghost'
}

// ── EscalationsCard ──────────────────────────────────────────────────────

export function EscalationsCard({ department }: { department: string }) {
  const [esc, setEsc] = useState<HubDeptEscalation[] | null>(null)
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const r = await hubAutonomy.escalations(department).catch(() => null)
      if (!cancelled) setEsc(r?.escalations ?? [])
    }
    load()
    const id = setInterval(load, REFRESH_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [department])

  return (
    <Card title="Escalations" endpoint={`/hub/departments/${department}/escalations`} bodyPad={false}>
      {esc === null && (
        <div style={{ padding: '14px 12px', color: 'var(--text-dim)', fontSize: 12 }}>Loading…</div>
      )}
      {esc !== null && esc.length === 0 && (
        <div style={{ padding: '14px 12px', color: 'var(--text-dim)', fontSize: 12 }}>
          No escalations in {department}.
        </div>
      )}
      {esc !== null && esc.length > 0 && esc.map((e, i) => (
        <div
          key={e.id}
          className="row"
          style={{
            padding: '8px 12px',
            borderTop: i ? '1px solid var(--border)' : 0,
            gap: 10,
          }}
        >
          <Pill variant={priorityTone(e.priority)}>{e.priority}</Pill>
          <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)', minWidth: 100 }}>
            {e.row_id || e.id.slice(0, 8)}
          </span>
          <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {e.title}
          </span>
          <Pill variant={e.status === 'blocked' ? 'err' : 'warn'}>{e.status}</Pill>
        </div>
      ))}
    </Card>
  )
}

// ── BudgetGauge ──────────────────────────────────────────────────────────

export function BudgetGauge({ department }: { department: string }) {
  const [agents, setAgents] = useState<HubAutonomyAgent[] | null>(null)
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const r = await hubAutonomy.agents({ department }).catch(() => null)
      if (!cancelled) setAgents(r?.agents ?? [])
    }
    load()
    const id = setInterval(load, REFRESH_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [department])

  const totalUSD = useMemo(() => {
    if (!agents) return 0
    return agents.reduce((acc, a) => acc + (Number(a.budget_usd_spent) || 0), 0)
  }, [agents])
  const totalTokens = useMemo(() => {
    if (!agents) return 0
    return agents.reduce((acc, a) => acc + (Number(a.budget_tokens_spent) || 0), 0)
  }, [agents])

  return (
    <Card title="Budget" endpoint={`/hub/agents?department=${department}`}>
      {agents === null && (
        <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Loading…</div>
      )}
      {agents !== null && agents.length === 0 && (
        <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>{PHASE2_EMPTY}</div>
      )}
      {agents !== null && agents.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div className="row" style={{ alignItems: 'baseline', gap: 8 }}>
            <span className="mono" style={{ fontSize: 18, fontWeight: 600 }}>
              {formatCurrency(totalUSD, { decimals: 2 })}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>spent · cycle</span>
          </div>
          <div className="row" style={{ alignItems: 'baseline', gap: 8 }}>
            <span className="mono" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {totalTokens.toLocaleString()}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>tokens</span>
          </div>
          <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>
            across {agents.length} agent{agents.length === 1 ? '' : 's'}
          </div>
        </div>
      )}
    </Card>
  )
}

// ── DepartmentObservability — packs all 6 tiles into a section ───────────

export function DepartmentObservability({ department }: { department: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="row" style={{ alignItems: 'baseline', gap: 8, padding: '4px 2px' }}>
        <Endpoint>Phase 2 · agent observability</Endpoint>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 10 }}>
        <ActiveAgentsCard department={department} />
        <BudgetGauge department={department} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <SessionsPanel department={department} />
        <ActivityStream department={department} />
      </div>
      <DeptQueuePanel department={department} />
      <EscalationsCard department={department} />
    </div>
  )
}

// ── helpers ──────────────────────────────────────────────────────────────

function relTime(iso: string): string {
  const t = new Date(iso).getTime()
  if (!isFinite(t)) return ''
  const diff = Date.now() - t
  const s = Math.floor(diff / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 48) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}
